// Pure helpers for splitting and merging expense/income/work-order lines.
// Kept free of util-formaters imports so they stay unit-testable under vitest --
// anything reaching it (Field, FieldsManager, util-helpers) crashes vitest at
// util-constants:1100.
//
// Shared by dialogs/income-expense-dialog (via invoice-expense-item-table) and
// work-order-pay/wo-pay-process-bill. The rule both encode: manipulating lines
// only redistributes the split between them -- the grand total never changes.

const DESCRIPTION_SEPARATOR = '; '

/** Money value (number, or money-field string) to integer cents. */
export function toCents(value) {
    const number = Number(value)

    return Number.isFinite(number) ? Math.round(number * 100) : 0
}

/** Integer cents back to a money value. */
export function centsToMoney(cents) {
    return cents / 100
}

/**
 * Divide a subtotal into `count` shares rounded to 2 decimals.
 * Any rounding remainder is added to the last share so the shares sum exactly
 * to the subtotal (mirrors the payroll advance split behaviour).
 *
 * Works in integer cents. Dividing in floats let `base` round *up* -- splitting
 * 0.05 into 10 produced nine shares of 0.01 and a last share of -0.04 to keep
 * the sum exact. Cents make every share land on the same side of zero as the
 * subtotal, and remove float drift on long splits.
 *
 * @param {number|string} subtotal - the line subtotal to divide
 * @param {number} count - number of entries to split into
 * @returns {number[]} array of `count` shares
 */
export function computeSplitAmounts(subtotal, count) {
    const totalCents = toCents(subtotal)
    const n = Math.max(1, Math.floor(count))

    const sign = totalCents < 0 ? -1 : 1
    const absolute = Math.abs(totalCents)
    const base = Math.floor(absolute / n)
    const remainder = absolute - (base * n)

    return Array.from({length: n}, (_, index) => (
        centsToMoney(sign * (index === n - 1 ? base + remainder : base))
    ))
}

/**
 * Zero-padded "NN/XX" label so split entries sort correctly by ASCII
 * (e.g. 01/12, 02/12 … 12/12). Padding width tracks the number of digits in
 * `count`, so single-digit splits stay unpadded.
 *
 * @param {number} index - 1-based entry index
 * @param {number} count - total number of entries
 * @returns {string}
 */
export function buildSplitLabel(index, count) {
    const width = String(count).length
    return `${String(index).padStart(width, '0')}/${count}`
}

/**
 * Concatenate the descriptions of merged lines, dropping the blank ones.
 *
 * @param {Array<string|null|undefined>} descriptions
 * @returns {string}
 */
export function mergeDescriptions(descriptions = []) {
    return descriptions
        .map(it => (it ?? '').toString().trim())
        .filter(it => !!it)
        .join(DESCRIPTION_SEPARATOR)
}
